import { useEffect, useState, type ReactNode } from "react";
import { listAllEvents, type AgendaMark } from "./agenda";
import { MailBlock } from "./MailBlock";
import { listFicheContacts, noteVisit, saveFiche } from "./api";
import { Atelier, CoffreCreateButtons, type AtelierStart } from "./Atelier";
import { CoffreBlock } from "./CoffreBlock";
import { FactureBoard } from "./FactureBoard";
import { FactureTab } from "./FactureTab";
import { NoteBlock } from "./NoteBlock";
import { StageBar } from "./Suivi";
import { RelanceBoard } from "./Relances";
import { SignBlock, SignPicker } from "./SignBlock";
import { RdvSheet } from "./RdvSheet";
import type { CrmFiche } from "./types";

const WEEK = ["L", "M", "M", "J", "V", "S", "D"];

export function Cockpit({ fiche, onChange }: { fiche: CrmFiche; onChange: (fiche: CrmFiche) => void }) {
  const folder = fiche.coffre_path || "Dossier Coffre";
  const [signOpen, setSignOpen] = useState(false);
  const [atelier, setAtelier] = useState<AtelierStart | null>(null);
  const [mailSending, setMailSending] = useState(false);
  const [marks, setMarks] = useState<AgendaMark[]>([]);
  const [mails, setMails] = useState<string[]>(() => [fiche.email].filter((item) => item.trim() !== ""));
  const [agendaTick, setAgendaTick] = useState(0);
  const linked = fiche.facturx_client_id > 0;
  const coffreOn = fiche.coffre_path !== "";
  const signFolder = fiche.sign_folder_id ?? 0;

  useEffect(() => {
    void noteVisit(fiche.id).catch(() => undefined);
    let alive = true;
    setMails([fiche.email].filter((item) => item.trim() !== ""));
    void listFicheContacts(fiche.id).then((rows) => {
      if (!alive) return;
      const next = [...new Set([fiche.email, ...rows.map((row) => row.email)].map((item) => item.trim()).filter((item) => item.includes("@")))];
      setMails(next);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [fiche.id, fiche.email]);

  useEffect(() => {
    let alive = true;
    void listAllEvents()
      .then((rows) => {
        if (alive) setMarks(rows);
      })
      .catch(() => {
        if (alive) setMarks([]);
      });
    return () => {
      alive = false;
    };
  }, [fiche.id, agendaTick]);

  useEffect(() => {
    const bump = () => setAgendaTick((value) => value + 1);
    window.addEventListener("crm-agenda-refresh", bump);
    return () => window.removeEventListener("crm-agenda-refresh", bump);
  }, []);

  return (
    <div className="crm-rise grid gap-3 px-6 pb-10 pt-5">
      <NoteBlock key={fiche.id} folder={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} ficheId={fiche.id} origin={{ id: fiche.id, name: fiche.name, logo: fiche.logo_path }} />
      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_22rem_22rem]">
        <div className="relative h-[26.5rem]">
          <div className="absolute inset-0 flex min-h-0">
            <Block title="Coffre" detail={folder} fill extra={<CoffreCreateButtons disabled={!coffreOn} onCreate={setAtelier} />}>
              {fiche.coffre_path ? <CoffreBlock home={fiche.coffre_path} ownerId={fiche.coffre_owner_id || 0} fiche={fiche} /> : <p className="text-sm text-slate-500">Le dossier de la fiche dans CRM clients n’est pas prêt.</p>}
            </Block>
          </div>
        </div>
        <AgendaZone fiche={fiche} marks={marks} />
      </div>
      <StageBar companyId={fiche.facturx_company_id} clientId={fiche.facturx_client_id} mine={fiche.mine} />
      <RelanceBoard />
      <Block title="Factures">
        {linked ? <FactureBoard fiche={fiche} /> : <FactureTab fiche={fiche} onChange={onChange} onLinked={() => undefined} />}
      </Block>
      <div className="grid gap-3 md:grid-cols-2">
        <Block title="Mail" detail={fiche.email || "Pas d’e-mail sur la fiche"} badge={mailSending ? <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600"><span className="crm-link-spin h-3.5 w-3.5 rounded-full border border-current border-t-transparent" aria-hidden />Envoi en cours</span> : null}>
          <MailBlock key={`${fiche.id}-${mails.join("|")}`} emails={mails} onSending={setMailSending} />
        </Block>
        <Block title="Signature" detail={signFolder === 0 ? "Tous les contrats" : "Dossier"} extra={<button type="button" className="crm-pill rounded-full px-2.5 py-1 text-xs font-semibold" onClick={() => setSignOpen(true)}>Dossier</button>}>
          <SignBlock key={`${fiche.id}-${signFolder}`} folderId={signFolder} companyId={fiche.facturx_company_id} clientId={fiche.facturx_client_id} email={fiche.email} phone={fiche.phone} ficheName={fiche.name} ficheId={fiche.id} />
        </Block>
      </div>
      {atelier && fiche.coffre_path ? <Atelier key={atelier} fiche={fiche} start={atelier} onClose={() => setAtelier(null)} /> : null}
      {signOpen ? (
        <SignPicker
          onClose={() => setSignOpen(false)}
          onChoose={async (folderId) => {
            onChange(await saveFiche({ ...fiche, sign_folder_id: folderId }));
          }}
        />
      ) : null}
    </div>
  );
}

function Block({
  title,
  detail,
  square,
  fill,
  badge,
  extra,
  children,
}: {
  title: string;
  detail?: string;
  square?: boolean;
  fill?: boolean;
  badge?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  const frame = square
    ? "crm-card flex h-[26.5rem] w-full shrink-0 flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]"
    : fill
      ? "crm-card flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]"
      : "crm-card flex min-h-36 flex-col overflow-hidden rounded-2xl border border-slate-200 shadow-[0_10px_28px_rgb(15_23_42/7%)]";
  return (
    <section className={frame}>
      <h2 className="crm-head flex shrink-0 items-baseline gap-2 border-b px-4 py-2 text-sm font-semibold">
        <span className="truncate">{title}</span>
        {detail ? <span className="min-w-0 truncate text-xs font-medium opacity-70">{detail}</span> : null}
        {badge || extra ? (
          <span className="ml-auto flex shrink-0 items-center gap-1.5">
            {badge}
            {extra}
          </span>
        ) : null}
      </h2>
      <div className={square ? "min-h-0 flex-1 overflow-hidden p-3" : fill ? "min-h-0 flex-1 overflow-hidden p-4" : "min-h-0 flex-1 overflow-auto p-4"}>{children}</div>
    </section>
  );
}

function AgendaZone({ fiche, marks }: { fiche: CrmFiche; marks: AgendaMark[] }) {
  const [cursor, setCursor] = useState(() => new Date());
  const [picked, setPicked] = useState(todayStamp);
  const month = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const dayEvents = marks.filter((item) => item.day === picked).sort((a, b) => a.time.localeCompare(b.time));

  return (
    <div className="contents">
      <Block title="Agenda" square>
        <div className="relative flex h-full flex-col">
          <div className="mb-1 flex items-center justify-between">
            <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Mois précédent" onClick={() => { setPicked(""); setCursor(new Date(month.getFullYear(), month.getMonth() - 1, 1)); }}>
              ‹
            </button>
            <p className="text-sm font-semibold capitalize">{monthLabel(month)}</p>
            <button type="button" className="grid h-7 w-7 place-items-center rounded-full text-slate-500 hover:bg-black/5" aria-label="Mois suivant" onClick={() => { setPicked(""); setCursor(new Date(month.getFullYear(), month.getMonth() + 1, 1)); }}>
              ›
            </button>
          </div>
          <Month month={month} marks={marks} picked={picked} onPick={setPicked} />
        </div>
      </Block>
      <Block title="RDV" detail={picked ? dayLabel(picked) : "Choisir un jour"} square>
        <div className="flex h-full min-h-0 flex-col">
          {picked && dayEvents.length === 0 ? <p className="text-sm text-slate-500">Rien ce jour.</p> : null}
          {!picked ? <p className="text-sm text-slate-500">Choisis un jour dans l’agenda.</p> : null}
          <ul className="crm-scroll min-h-0 flex-1 space-y-2 overflow-auto">
            {dayEvents.map((item) => (
              <li key={item.id} className="flex items-start gap-2 rounded-xl border border-slate-200 px-3 py-2">
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: item.color }} />
                <span className="min-w-0">
                  <span className="block text-xs font-semibold">{item.time || "Journée"}</span>
                  <span className="block truncate text-sm">{item.title}</span>
                </span>
              </li>
            ))}
          </ul>
          <RdvLaunch fiche={fiche} />
        </div>
      </Block>
    </div>
  );
}

function RdvLaunch({ fiche }: { fiche: CrmFiche }) {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");

  return (
    <div className="mt-2">
      <button type="button" className="crm-pill inline-flex w-full items-center justify-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold" onClick={() => setOpen(true)}>
        Proposer un RDV
      </button>
      {sending ? <p className="mt-1.5 inline-flex w-full items-center justify-center gap-1.5 text-xs font-semibold text-emerald-600"><span className="crm-link-spin h-3.5 w-3.5 rounded-full border border-current border-t-transparent" aria-hidden />Envoi en cours</p> : null}
      {notice ? <p className="mt-1 text-center text-[11px] text-slate-600">{notice}</p> : null}
      {error ? <p className="mt-1 text-center text-[11px] text-rose-700">{error}</p> : null}
      {open ? <RdvSheet fiche={fiche} onClose={() => setOpen(false)} onSending={(busy) => { setSending(busy); if (busy) { setError(""); setNotice(""); } }} onError={setError} onDone={setNotice} /> : null}
    </div>
  );
}

function Month({ month, marks, picked, onPick }: { month: Date; marks: AgendaMark[]; picked: string; onPick: (day: string) => void }) {
  const year = month.getFullYear();
  const index = month.getMonth();
  const today = new Date();
  const first = new Date(year, index, 1);
  const lead = (first.getDay() + 6) % 7;
  const days = new Date(year, index + 1, 0).getDate();
  const cells = [...Array(lead).fill(0), ...Array.from({ length: days }, (_, i) => i + 1)];

  return (
    <div className="grid min-h-0 flex-1 grid-cols-7 content-start gap-0.5 text-center text-xs" style={{ gridTemplateRows: `auto repeat(${Math.ceil(cells.length / 7)}, minmax(0, 1fr))` }}>
      {WEEK.map((label, i) => (
        <b key={`${label}-${i}`} className="crm-ink py-1 text-[11px] font-semibold uppercase">
          {label}
        </b>
      ))}
      {cells.map((day, i) => {
        const stamp = day > 0 ? `${year}-${String(index + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` : "";
        const on = stamp !== "" && today.getFullYear() === year && today.getMonth() === index && today.getDate() === day;
        const chosen = stamp !== "" && stamp === picked;
        const dot = marks.find((item) => item.day === stamp);
        return (
          <button key={i} type="button" disabled={!stamp} className="relative grid h-full min-h-8 place-items-center disabled:cursor-default" onClick={() => stamp && onPick(stamp)}>
            <span className={chosen ? "crm-soft relative grid h-8 w-8 place-items-center rounded-full font-semibold" : on ? "crm-fill relative grid h-8 w-8 place-items-center rounded-full font-semibold text-white" : "relative grid h-8 w-8 place-items-center text-slate-700"}>
              {day || ""}
              {dot ? <span className="absolute bottom-0.5 h-1 w-1 rounded-full" style={{ background: dot.color }} /> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function todayStamp(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

function monthLabel(date: Date): string {
  return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

function dayLabel(stamp: string): string {
  const [year, month, day] = stamp.split("-").map(Number);
  if (!year || !month || !day) return "";
  return new Date(year, month - 1, day).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
}
